package com.example.mystybag.login.views

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.graphics.BlendMode
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.FilterQuality
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.layout.ContentScale
import com.example.mystybag.common.extensions.AsyncImage

/**
 * JS actual: Compose-rendered welcome hero image with the Figma fade.
 *
 * The previous version drew a native <img> on a DOM overlay so the bytes
 * could bypass CORS. Now that the asset origin allows CORS, Coil decodes
 * the image and paints it on the Skiko canvas like every other platform.
 * The fade-to-transparent at the bottom is done with `BlendMode.DstIn`
 * over a vertical gradient mask, exactly mirroring the previous CSS
 * `mask-image: linear-gradient(...)` declaration.
 */
@Composable
actual fun WelcomeFadingImage(url: String, modifier: Modifier) {
    Box(
        modifier = modifier
            .graphicsLayer {
                // Force an offscreen compositing layer so BlendMode.DstIn
                // applies to the image content only, not the parent.
                alpha = 0.999f
            }
            .drawWithContent {
                drawContent()
                drawRect(
                    brush = Brush.verticalGradient(
                        colorStops = arrayOf(
                            0.00f to Color.Black,
                            0.55f to Color.Black,
                            0.78f to Color.Transparent,
                            1.00f to Color.Transparent
                        )
                    ),
                    blendMode = BlendMode.DstIn
                )
            }
    ) {
        AsyncImage(
            link = url,
            modifier = Modifier.fillMaxSize(),
            contentScale = ContentScale.Crop,
            alignment = Alignment.TopCenter,
            placeholder = null,
            // Default AsyncImage uses FilterQuality.Low + decode-to-layout-size, which looks soft on a large hero.
            filterQuality = FilterQuality.High,
            useOriginalSize = true
        )
    }
}
